import * as React from 'react';
import styled from '@emotion/styled';
import H3 from '@base/H3/H3';
import P1 from '@base/P1/P1';
import { Fonts, Spacings } from '@styles/index';
import Checkbox from '@base/Checkbox/Checkbox';
import Image from '@base/Image';
import CheckboxToggle from '@icon/CheckboxToggle';
import CheckboxChecked from '@icon/CheckboxChecked';
import Button from '@base/Button/Button';

interface IBatchJoinConfirmationDrawerProps {
  open: boolean;
  onConfirmation: () => void;
}

interface IColor {
  $color: string;
}

const StyH3 = styled(H3)`
  font-weight: ${Fonts.HEAVY};
  line-height: 28px;
`;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StyP1 = styled(P1)`
  font-weight: ${Fonts.LIGHT};
  line-height: 18px;
  margin-top: ${Spacings.SPACING_4B};
`;

const InfoWrapper = styled.div`
  max-width: 332px;
`;

const CheckboxWrapper = styled.div`
  margin-top: ${Spacings.SPACING_14B};
  margin-bottom: ${Spacings.SPACING_6B};
`;

const StyCheckbox = styled(Checkbox)<IColor>`
  .MuiFormControlLabel-label {
    font-weight: ${Fonts.SEMIBOLD};
    font-size: ${Fonts.P1};
    line-height: 18px;
    color: ${({ $color }) => $color};
  }
`;

const StyButton = styled(Button)`
  font-weight: ${Fonts.HEAVY};
  font-size: ${Fonts.P1};
  line-height: 14px;
`;

const OuterWrapper = styled.div`
  grid-column: span 10;
`;

const BatchJoinConfirmationDrawer: React.FC<
  IBatchJoinConfirmationDrawerProps
> = ({ open, onConfirmation }) => {
  const [checked, setChecked] = React.useState(false);
  const [error, setError] = React.useState(false);

  const onChange = (e) => {
    setChecked(e.target.checked);
    setError(false);
  };

  const onClick = () => {
    if (!checked) setError(true);
    if (checked) onConfirmation();
  };

  React.useEffect(() => {
    setChecked(false);
    setError(false);
  }, [open]);

  if (!open) return null;

  return (
    <>
      <OuterWrapper>
        <Wrapper>
          <InfoWrapper>
            <StyH3 color="var(--color-text-primary)">
              We recommend learning from only 1 batch
            </StyH3>
            <StyP1 color="var(--color-text-secondary)">
              Joining more than 1 batch will clutter your planner with a lot of
              classes
            </StyP1>
          </InfoWrapper>
          <Image
            src="batch-warning.svg"
            height={Spacings.SPACING_20B}
            width={Spacings.SPACING_20B}
            alt="batch"
          />
        </Wrapper>
        <CheckboxWrapper>
          <StyCheckbox
            label="I’m okay adding more classes to my planner"
            checked={checked}
            onChange={onChange}
            $color={error ? 'var(--color-i-red)' : 'var(--color-text-primary)'}
            // @ts-ignore
            icon={
              <CheckboxToggle
                color={error ? 'var(--color-i-red)' : 'var(--color-divider)'}
                fill="var(--color-base-1)"
                height={Spacings.SPACING_6B}
                width={Spacings.SPACING_6B}
              />
            }
            checkedIcon={
              <CheckboxChecked
                color="var(--color-text-primary)"
                fill="var(--color-base-1)"
                height={Spacings.SPACING_6B}
                width={Spacings.SPACING_6B}
              />
            }
          />
        </CheckboxWrapper>
        <StyButton
          // @ts-ignore
          label="Join anyway"
          variant="hollow"
          color="red"
          grid={2}
          width="176px"
          onClick={onClick}
        />
      </OuterWrapper>
    </>
  );
};

export default BatchJoinConfirmationDrawer;
